'use client';

import React, { ReactNode, useState } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { useConvert } from '@/api/trading/useConvert';
import { Container } from '@/blocks/components/Container';
import { TextField } from '@/blocks/components/Controls/TextField';
import { useFormatter } from '@/blocks/formatters/format-number';
import { EventBlockNames } from '@/providers/analytics/types';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';
import { extractNumber, isFiniteNumber } from '@/utils/number';

import { ErrorMessage } from '@/app/[lang]/converters/sections/CurrencyConverter/ErrorMessage';
import {
  WrapperConverterContent,
  WrapperConverterFields,
} from '@/app/[lang]/converters/sections/CurrencyConverter/Wrappers';
import { ConverterCurrency } from '@/app/[lang]/converters/types';

import { CurrencyConverterSelect } from './CurrencyConverterSelect';
import { ConverterResultPanel } from './ResultPanel';
import styles from './styles.module.scss';

type Props = {
  title?: React.ReactNode;
  from?: string;
  to?: string;
  currencies: ConverterCurrency[];
  resultPanelSlot?: ReactNode;
};

const DEFAULT_BASE_AMOUNT = 1;

function notSuitableForInputValue(val: any) {
  return val === null || (typeof val === 'number' && !isFinite(val)) || typeof val === 'undefined';
}

function toInputValue(val: any): string {
  if (notSuitableForInputValue(val)) {
    return '';
  }

  return val.toString();
}

export function CurrencyConverter({ title, from, to, currencies, resultPanelSlot }: Props) {
  const tr = useTranslations();
  const [fromCurrency, setFromCurrency] = useState(toInputValue(from));
  const [toCurrency, setToCurrency] = useState(toInputValue(to));
  const [baseAmount, setBaseAmount] = useState<string>(toInputValue(DEFAULT_BASE_AMOUNT));
  const formatter = useFormatter({
    maximumFractionDigits: 20,
  });

  const { rate, amount, error } = useConvert({
    from: fromCurrency,
    to: toCurrency,
    baseAmount: extractNumber(parseFloat(baseAmount), DEFAULT_BASE_AMOUNT),
  });

  const handleReset = () => {
    setBaseAmount(toInputValue(DEFAULT_BASE_AMOUNT));
    setFromCurrency('');
    setToCurrency('');
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setBaseAmount(value);
    }
  };

  return (
    <Container
      tag={'section'}
      className={styles.wrapper}
      data-event-block-name={EventBlockNames.currencyConverter}
    >
      <div className={styles.wrapperFields}>
        <WrapperConverterContent>
          <div>
            <p className={typography.h3}>{title}</p>
            <p className={classNames(typography.headerSubhead, typography.subheadingM)}>
              {tr.rich('pages.converters.index.sections.converterFields.subtitle')}
            </p>
          </div>
          <WrapperConverterFields>
            <TextField
              label={tr.rich('pages.converters.index.sections.converterFields.field.amount')}
              variant="outlined"
              fullWidth
              value={toInputValue(baseAmount)}
              autoComplete={'off'}
              className={styles.field}
              onChange={handleAmountChange}
              InputProps={{
                inputMode: 'numeric',
              }}
            />
            <CurrencyConverterSelect
              value={fromCurrency}
              onChange={setFromCurrency}
              inputLabel={tr.rich('pages.converters.index.sections.converterFields.field.from')}
              currencies={currencies}
            />
            <CurrencyConverterSelect
              value={toCurrency}
              onChange={setToCurrency}
              inputLabel={tr.rich('pages.converters.index.sections.converterFields.field.to')}
              currencies={currencies}
            />
          </WrapperConverterFields>
          <div className={styles.wrapperBtns}>
            <button
              onClick={handleReset}
              className={classNames(buttons.btn, buttons.btnResponsiveSM, buttons.btnOutline)}
            >
              {tr.rich('pages.converters.index.sections.converterFields.btn.reset')}
            </button>
            <button
              className={classNames(buttons.btn, buttons.btnPrimary, buttons.btnResponsiveSM)}
              disabled={!fromCurrency || !toCurrency}
            >
              {tr.rich('pages.converters.index.sections.converterFields.btn.calculate')}
            </button>
          </div>
        </WrapperConverterContent>
        <div>{<ErrorMessage error={error} fromCurrency={fromCurrency} toCurrency={toCurrency} />}</div>
      </div>
      <ConverterResultPanel slot={resultPanelSlot}>
        <TextField
          className={classNames(styles.field, styles.resultField)}
          label={tr.rich('pages.converters.index.sections.converterFields.field.conversionRate')}
          variant="outlined"
          fullWidth
          value={isFiniteNumber(rate) ? formatter.format(rate) : ''}
          autoComplete={'off'}
          disabled
        />
        <TextField
          className={classNames(styles.field, styles.resultField)}
          autoComplete={'off'}
          label={tr.rich('pages.converters.index.sections.converterFields.field.total')}
          variant="outlined"
          fullWidth
          value={isFiniteNumber(amount) ? formatter.format(amount) : ''}
          disabled
        />
      </ConverterResultPanel>
    </Container>
  );
}
